import {
  PRIORITIES,
  PRIORITY_META,
  TASK_TYPES,
  TASK_TYPE_META,
  WAITING_FOR,
  type CreateTaskInput,
  type DateSpec,
  type FollowUpSpec,
  type Priority,
  type PresetDTO,
  type RecurrenceRule,
  type ReminderSpec,
  type TaskDetailDTO,
  type TaskType,
  type Visibility,
  type WaitingFor,
} from '@taskos/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, get, newIdempotencyKey, patch as apiPatch, post } from '../../api/client';
import { invalidateTasks, useCategories, useDirectory, usePresets, useProjects, useTask, useTagSuggestions } from '../../api/queries';
import { useSession } from '../../api/session';
import { useUI } from '../../app/ui';
import { useToast } from '../../components/toast';
import { Button, ErrorState, Field, Modal, Segmented, Skeleton, Toggle } from '../../components/ui';
import { todayISO } from '../../lib/format';
import { useUnsavedWarning } from '../../lib/hooks';
import { clearDraft, loadDraft, saveDraft } from '../../lib/storage';
import { DatePicker } from './DatePicker';
import { RecurrencePicker } from './RecurrencePicker';
import { ReminderPicker } from './ReminderPicker';

interface FormState {
  title: string;
  description: string;
  notes: string;
  priority: Priority;
  taskType: TaskType;
  visibility: Visibility;
  status: 'INBOX' | 'PLANNED' | 'IN_PROGRESS' | 'WAITING';
  projectId: string;
  categoryId: string;
  tags: string[];
  assigneeId: string;
  due: DateSpec;
  reminders: ReminderSpec[];
  recurrence: RecurrenceRule;
  followUpEnabled: boolean;
  followUp: FollowUpSpec;
  nextAction: string;
  nextActionDate: string;
  waitingFor: WaitingFor | '';
  waitingForNote: string;
  presetId: string;
}

const blank = (): FormState => ({
  title: '',
  description: '',
  notes: '',
  priority: 'MEDIUM',
  taskType: 'FIXED_DATE',
  visibility: 'TEAM',
  status: 'PLANNED',
  projectId: '',
  categoryId: '',
  tags: [],
  assigneeId: '',
  due: { mode: 'RELATIVE', amount: 0, workingDays: false },
  reminders: [],
  recurrence: { freq: 'DAILY', interval: 1, startDate: todayISO(), holidayPolicy: 'NONE' },
  followUpEnabled: false,
  followUp: { due: { mode: 'RELATIVE', amount: 7, workingDays: true } },
  nextAction: '',
  nextActionDate: '',
  waitingFor: '',
  waitingForNote: '',
  presetId: '',
});

function fromTask(t: TaskDetailDTO): FormState {
  const b = blank();
  return {
    ...b,
    title: t.title,
    description: t.description ?? '',
    notes: t.notes ?? '',
    priority: t.priority,
    taskType: t.taskType,
    visibility: t.visibility,
    status: (['INBOX', 'PLANNED', 'IN_PROGRESS', 'WAITING'].includes(t.status) ? t.status : 'PLANNED') as FormState['status'],
    projectId: t.project?.id ?? '',
    categoryId: t.category?.id ?? '',
    tags: t.tags,
    assigneeId: t.assignee?.id ?? '',
    due: t.dueDate ? { mode: 'DATE', date: t.dueDate, time: t.dueTime } : { mode: 'NONE' },
    reminders: t.reminders.filter((r) => r.status !== 'CANCELLED').map((r) => ({
      kind: r.kind,
      offsetMinutes: r.offsetMinutes,
      at: r.at,
      mode: r.mode,
      intensity: r.intensity,
      repeatEveryMinutes: r.repeatEveryMinutes,
    })),
    recurrence: t.recurrenceRule ?? b.recurrence,
    nextAction: t.nextAction ?? '',
    nextActionDate: t.nextActionDate ?? '',
    waitingFor: t.waitingFor ?? '',
    waitingForNote: t.waitingForNote ?? '',
  };
}

function applyPreset(s: FormState, p: PresetDTO): FormState {
  const c = p.config;
  return {
    ...s,
    title: s.title || c.title || '',
    description: s.description || c.description || '',
    priority: c.priority ?? s.priority,
    taskType: c.taskType ?? (c.due && c.due.mode !== 'NONE' ? 'FIXED_DATE' : s.taskType),
    visibility: c.visibility ?? s.visibility,
    due: c.due ?? s.due,
    reminders: c.reminders ?? s.reminders,
    followUpEnabled: !!c.followUp,
    followUp: c.followUp ?? s.followUp,
    recurrence: c.recurrence ? { ...s.recurrence, ...c.recurrence, startDate: c.recurrence.startDate ?? todayISO() } : s.recurrence,
    projectId: c.projectId ?? s.projectId,
    categoryId: c.categoryId ?? s.categoryId,
    tags: c.tags ?? s.tags,
    assigneeId: c.assigneeId ?? s.assigneeId,
    nextAction: c.nextAction ?? s.nextAction,
    waitingFor: c.waitingFor ?? s.waitingFor,
    presetId: p.id,
  };
}

const needsDate = (t: TaskType) => t === 'FIXED_DATE' || t === 'FOLLOW_UP' || t === 'QUICK';

export function TaskFormModal() {
  const ui = useUI();
  const st = ui.taskForm;
  if (!st) return null;
  return <TaskFormInner key={`${st.mode}:${st.taskId ?? 'new'}`} />;
}

function TaskFormInner() {
  const ui = useUI();
  const { me, can } = useSession();
  const st = ui.taskForm!;
  const isEdit = st.mode === 'edit';
  const task = useTask(isEdit ? st.taskId : undefined);
  const qc = useQueryClient();
  const toast = useToast();
  const directory = useDirectory();
  const projects = useProjects();
  const categories = useCategories();
  const presets = usePresets(true);
  const draftKey = isEdit ? `task-edit:${st.taskId}` : `task-create:${me.id}`;

  const [form, setForm] = useState<FormState | null>(null);
  const original = useRef<FormState | null>(null);
  const [restored, setRestored] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<TaskDetailDTO | null>(null);
  const [saving, setSaving] = useState(false);
  const idemKey = useRef(newIdempotencyKey());
  const [tagInput, setTagInput] = useState('');
  const tagSuggest = useTagSuggestions(tagInput.trim());

  // Initialise (and restore an unsaved draft if one exists).
  useEffect(() => {
    if (form) return;
    if (isEdit) {
      if (!task.data) return;
      const base = fromTask(task.data);
      original.current = base;
      const d = loadDraft<{ version: number; form: FormState }>(draftKey);
      if (d && d.version === task.data.version) {
        setForm(d.form);
        setRestored(true);
      } else setForm(base);
    } else {
      const base = { ...blank(), ...(st.initial as Partial<FormState> | undefined) };
      original.current = base;
      const d = loadDraft<FormState>(draftKey);
      if (d && !st.initial) {
        setForm(d);
        setRestored(true);
      } else setForm(base);
    }
  }, [isEdit, task.data, form, draftKey, st.initial]);

  // The form renders one tick after the modal opens — move focus to the title (or the requested
  // section) once it exists, instead of leaving it on the Close button.
  const ready = !!form;
  useEffect(() => {
    if (!ready) return;
    const target = st.focusSection === 'reminders' ? document.querySelector<HTMLElement>('#tf-reminders button') : document.getElementById('tf-title');
    target?.focus();
    if (st.focusSection) target?.scrollIntoView({ block: 'center' });
  }, [ready, st.focusSection]);

  const dirty = useMemo(() => !!form && !!original.current && JSON.stringify(form) !== JSON.stringify(original.current), [form]);
  useUnsavedWarning(dirty);
  useEffect(() => {
    if (!form || !dirty) return;
    const t = setTimeout(() => saveDraft(draftKey, isEdit ? { version: task.data?.version, form } : form), 400);
    return () => clearTimeout(t);
  }, [form, dirty, draftKey, isEdit, task.data?.version]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  const close = () => ui.closeTaskForm();
  const discardDraft = () => {
    clearDraft(draftKey);
    setForm(original.current);
    setRestored(false);
  };

  const validate = (f: FormState): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!f.title.trim()) e.title = 'Title is required';
    if (f.title.length > 300) e.title = 'Title is too long (max 300)';
    if (needsDate(f.taskType) && f.due.mode === 'NONE' && f.taskType !== 'QUICK') e.due = 'Pick a due date for this task type';
    if (f.status === 'WAITING' && !f.waitingFor) e.waitingFor = 'Who are you waiting for?';
    if (f.taskType === 'RECURRING' && f.recurrence.freq === 'WEEKLY' && f.recurrence.weekdays?.length === 0) e.recurrence = 'Pick at least one weekday';
    if (f.followUpEnabled && f.followUp.due.mode === 'NONE') e.followUp = 'Pick a follow-up date';
    return e;
  };

  const toCreateInput = (f: FormState): CreateTaskInput => ({
    title: f.title.trim(),
    description: f.description.trim() || null,
    notes: f.notes.trim() || null,
    priority: f.priority,
    taskType: f.taskType,
    visibility: f.visibility,
    status: f.status,
    projectId: f.projectId || null,
    categoryId: f.categoryId || null,
    tags: f.tags,
    assigneeId: f.assigneeId || null,
    due: f.taskType === 'RECURRING' || f.taskType === 'ANYTIME' || f.taskType === 'ONGOING' ? (f.taskType === 'RECURRING' ? { mode: 'NONE' } : f.due) : f.due,
    reminders: f.reminders,
    recurrence: f.taskType === 'RECURRING' ? f.recurrence : null,
    followUp: f.followUpEnabled ? f.followUp : null,
    nextAction: f.nextAction.trim() || null,
    nextActionDue: f.nextActionDate ? { mode: 'DATE', date: f.nextActionDate } : null,
    waitingFor: f.status === 'WAITING' ? (f.waitingFor as WaitingFor) : f.waitingFor ? (f.waitingFor as WaitingFor) : null,
    waitingForNote: f.waitingForNote.trim() || null,
    presetId: f.presetId || null,
  });

  /** Only changed fields are sent on edit — an untouched due date is never re-resolved. */
  const toPatch = (f: FormState, o: FormState, version: number) => {
    const p: Record<string, unknown> = { version };
    const ch = <K extends keyof FormState>(k: K) => JSON.stringify(f[k]) !== JSON.stringify(o[k]);
    if (ch('title')) p.title = f.title.trim();
    if (ch('description')) p.description = f.description.trim() || null;
    if (ch('notes')) p.notes = f.notes.trim() || null;
    if (ch('priority')) p.priority = f.priority;
    if (ch('taskType')) p.taskType = f.taskType;
    if (ch('visibility')) p.visibility = f.visibility;
    if (ch('status')) p.status = f.status;
    if (ch('projectId')) p.projectId = f.projectId || null;
    if (ch('categoryId')) p.categoryId = f.categoryId || null;
    if (ch('tags')) p.tags = f.tags;
    if (ch('assigneeId')) p.assigneeId = f.assigneeId || null;
    if (ch('due')) p.due = f.due;
    if (ch('reminders')) p.reminders = f.reminders;
    if (ch('nextAction')) p.nextAction = f.nextAction.trim() || null;
    if (ch('nextActionDate')) p.nextActionDue = f.nextActionDate ? { mode: 'DATE', date: f.nextActionDate } : null;
    if (ch('waitingFor')) p.waitingFor = f.waitingFor || null;
    if (ch('waitingForNote')) p.waitingForNote = f.waitingForNote.trim() || null;
    return p;
  };

  const save = async () => {
    if (!form || saving) return;
    const e = validate(form);
    setErrors(e);
    setFormError(null);
    if (Object.keys(e).length) {
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const t = task.data!;
        const body = toPatch(form, original.current!, t.version);
        if (Object.keys(body).length === 1) {
          close();
          return;
        }
        await apiPatch(`/api/tasks/${t.id}`, body);
        toast.push({ kind: 'success', title: '✓ Saved', message: form.title });
      } else {
        const created = await post<{ id: string; ref: string }>('/api/tasks', toCreateInput(form), { idempotencyKey: idemKey.current });
        toast.push({ kind: 'success', title: `✓ Created ${created.ref}`, message: form.title });
      }
      clearDraft(draftKey);
      invalidateTasks(qc, st.taskId);
      close();
    } catch (err) {
      if (err instanceof ApiError && err.isConflict && isEdit) {
        const latest = await qc.fetchQuery({ queryKey: ['task', st.taskId], queryFn: () => get<TaskDetailDTO>(`/api/tasks/${st.taskId}`) });
        setConflict(latest);
      } else if (err instanceof ApiError && err.status === 422) {
        const fe = (err.details as { fieldErrors?: Record<string, string[]> } | undefined)?.fieldErrors ?? {};
        setErrors(Object.fromEntries(Object.entries(fe).map(([k, v]) => [k, v[0] ?? 'Invalid'])));
        setFormError(err.message);
      } else {
        // Keep everything the user typed; offer retry with the SAME idempotency key (no duplicates).
        setFormError(err instanceof Error ? err.message : 'We could not save this task.');
      }
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? `Edit ${task.data?.ref ?? 'task'}` : 'New task';
  const footer = form && (
    <>
      <span className="left">
        <span className="kbd">Ctrl</span>+<span className="kbd">Enter</span> to save
      </span>
      <Button onClick={close}>Cancel</Button>
      <Button variant="primary" onClick={save} loading={saving} disabled={!!conflict}>
        {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create task'}
      </Button>
    </>
  );

  return (
    <Modal open onClose={close} title={title} size="wide" dirty={dirty} footer={footer} initialFocus="#tf-title">
      {isEdit && task.isPending ? (
        <Skeleton rows={6} />
      ) : isEdit && task.isError ? (
        <ErrorState error={task.error} onRetry={() => task.refetch()} />
      ) : !form ? (
        <Skeleton rows={6} />
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void save();
            }
          }}
          noValidate
        >
          {restored && (
            <div className="inline-alert info" role="status">
              <RotateCcw size={16} aria-hidden />
              <span className="spacer">Restored your unsaved draft.</span>
              <button type="button" className="link-btn" onClick={discardDraft}>
                Discard draft
              </button>
            </div>
          )}
          {conflict && (
            <div className="inline-alert warning" role="alert">
              <div className="stack sm w-full">
                <strong>This task was updated by someone else.</strong>
                <ConflictDiff mine={form} theirs={fromTask(conflict)} />
                <div className="inline-row">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      const fresh = fromTask(conflict);
                      original.current = fresh;
                      setForm(fresh);
                      setConflict(null);
                      clearDraft(draftKey);
                      invalidateTasks(qc, st.taskId);
                    }}
                  >
                    Reload latest
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      // Keep my edits on top of the latest version: rebase original to latest.
                      original.current = fromTask(conflict);
                      qc.setQueryData(['task', st.taskId], conflict);
                      setConflict(null);
                    }}
                  >
                    Keep my changes
                  </Button>
                </div>
              </div>
            </div>
          )}
          {formError && (
            <div className="inline-alert danger" role="alert">
              <span className="spacer">
                ⚠️ {formError} <span className="muted">Your entered information is still here.</span>
              </span>
              <Button size="sm" onClick={save}>
                Retry
              </Button>
            </div>
          )}

          <Field label="Title" htmlFor="tf-title" error={errors.title}>
            <input
              id="tf-title"
              className="input"
              value={form.title}
              maxLength={300}
              placeholder="What needs to be done?"
              aria-invalid={!!errors.title}
              onChange={(e) => set('title', e.target.value)}
              autoComplete="off"
            />
          </Field>

          {!isEdit && presets.data && presets.data.length > 0 && (
            <div className="stack sm">
              <span className="label">📌 Use preset</span>
              <div className="preset-grid">
                {presets.data.map((p) => (
                  <button key={p.id} type="button" className="type-card" aria-checked={form.presetId === p.id} role="radio" onClick={() => setForm(applyPreset(form, p))}>
                    <strong>
                      {p.emoji ?? '📌'} {p.name}
                    </strong>
                    <span>{p.scope === 'GLOBAL' ? 'Team preset' : 'My preset'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="stack sm">
            <span className="label" id="tf-type">
              Task type
            </span>
            <div className="type-grid" role="radiogroup" aria-labelledby="tf-type">
              {TASK_TYPES.map((tt) => (
                <button key={tt} type="button" role="radio" className="type-card" aria-checked={form.taskType === tt} onClick={() => set('taskType', tt)} disabled={isEdit && tt === 'RECURRING' && form.taskType !== 'RECURRING'}>
                  <strong>
                    {TASK_TYPE_META[tt].emoji} {TASK_TYPE_META[tt].label}
                  </strong>
                  <span>{TASK_TYPE_META[tt].hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="stack sm">
            <span className="label" id="tf-prio">
              Priority
            </span>
            <div className="chip-row" role="radiogroup" aria-labelledby="tf-prio">
              {PRIORITIES.map((p) => (
                <button key={p} type="button" role="radio" className="chip" aria-checked={form.priority === p} onClick={() => set('priority', p)}>
                  {PRIORITY_META[p].emoji} {PRIORITY_META[p].label}
                </button>
              ))}
            </div>
          </div>

          {form.taskType === 'RECURRING' && !isEdit ? (
            <>
              <RecurrencePicker value={form.recurrence} onChange={(r) => set('recurrence', r)} />
              {errors.recurrence && <span className="error-text">{errors.recurrence}</span>}
            </>
          ) : form.taskType === 'RECURRING' && isEdit ? (
            <div className="inline-alert info small">This is one occurrence of a recurring schedule. Edit this occurrence's date below, or pause/resume the schedule from the task page.</div>
          ) : null}

          {(form.taskType !== 'RECURRING' || isEdit) && (
            <div id="tf-due">
              <DatePicker value={form.due} onChange={(d) => set('due', d)} allowNone={!needsDate(form.taskType) || form.taskType === 'QUICK'} />
              {errors.due && <span className="error-text">{errors.due}</span>}
            </div>
          )}

          <div id="tf-reminders">
            <ReminderPicker value={form.reminders} onChange={(r) => set('reminders', r)} hasDue={form.due.mode !== 'NONE' || form.taskType === 'RECURRING'} />
          </div>

          <div className="form-grid">
            <Field label="Assigned to" htmlFor="tf-assignee" hint={!can('task.assign') ? 'You can assign tasks to yourself' : undefined}>
              <select id="tf-assignee" className="select" value={form.assigneeId} onChange={(e) => set('assigneeId', e.target.value)}>
                <option value="">Unassigned</option>
                {(directory.data ?? [])
                  .filter((u) => u.isActive && (can('task.assign') || u.id === me.id || u.id === form.assigneeId))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                      {u.id === me.id ? ' (me)' : ''}
                    </option>
                  ))}
              </select>
            </Field>
            <div className="field">
              <span className="label">Visibility</span>
              <Segmented
                label="Visibility"
                value={form.visibility}
                onChange={(v) => set('visibility', v)}
                options={[
                  { value: 'TEAM', label: '👥 Team' },
                  { value: 'PERSONAL', label: '🙋 Personal' },
                  { value: 'PRIVATE', label: <><Lock size={13} aria-hidden /> Private</> },
                ]}
              />
              <span className="hint">{form.visibility === 'PRIVATE' ? 'Only you (and the assignee) can see it.' : form.visibility === 'PERSONAL' ? 'You and managers can see it.' : 'Visible to the team.'}</span>
            </div>
            <Field label="Project" htmlFor="tf-project">
              <select id="tf-project" className="select" value={form.projectId} onChange={(e) => set('projectId', e.target.value)}>
                <option value="">—</option>
                {(projects.data ?? []).filter((p) => !p.isArchived || p.id === form.projectId).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Category" htmlFor="tf-category">
              <select id="tf-category" className="select" value={form.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
                <option value="">—</option>
                {(categories.data ?? []).filter((c) => !c.isArchived || c.id === form.categoryId).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="field span-2">
              <label htmlFor="tf-tags">Tags</label>
              <div className="chip-row">
                {form.tags.map((t) => (
                  <span key={t} className="chip active">
                    #{t}
                    <button type="button" className="link-btn x" aria-label={`Remove tag ${t}`} onClick={() => set('tags', form.tags.filter((x) => x !== t))}>
                      ×
                    </button>
                  </span>
                ))}
                <input
                  id="tf-tags"
                  className="input"
                  style={{ width: 180 }}
                  list="tf-tag-list"
                  placeholder="Add tag, press Enter"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                      e.preventDefault();
                      const tag = tagInput.trim().replace(/^#/, '').slice(0, 40);
                      if (tag && !form.tags.some((x) => x.toLowerCase() === tag.toLowerCase()) && form.tags.length < 20) set('tags', [...form.tags, tag]);
                      setTagInput('');
                    }
                  }}
                />
                <datalist id="tf-tag-list">
                  {(tagSuggest.data ?? []).map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </div>
            </div>
          </div>

          <div className="form-grid">
            <div className="field">
              <span className="label">Status</span>
              <Segmented
                label="Status"
                value={form.status}
                onChange={(s) => set('status', s)}
                options={[
                  { value: 'INBOX', label: 'Inbox' },
                  { value: 'PLANNED', label: 'Planned' },
                  { value: 'IN_PROGRESS', label: 'In progress' },
                  { value: 'WAITING', label: 'Waiting' },
                ]}
              />
            </div>
            {form.status === 'WAITING' && (
              <div className="field">
                <span className="label" id="tf-wait">
                  Waiting for
                </span>
                <div className="chip-row" role="radiogroup" aria-labelledby="tf-wait" aria-invalid={!!errors.waitingFor}>
                  {WAITING_FOR.map((w) => (
                    <button key={w} type="button" role="radio" className="chip" aria-checked={form.waitingFor === w} onClick={() => set('waitingFor', w)}>
                      {w.charAt(0) + w.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
                <input className="input" placeholder="e.g. HDFC" value={form.waitingForNote} maxLength={200} onChange={(e) => set('waitingForNote', e.target.value)} aria-label="Waiting for (details)" />
                {errors.waitingFor && <span className="error-text">{errors.waitingFor}</span>}
              </div>
            )}
            <Field label="Next action" htmlFor="tf-next">
              <input id="tf-next" className="input" value={form.nextAction} maxLength={500} placeholder="e.g. Call bank" onChange={(e) => set('nextAction', e.target.value)} />
            </Field>
            <Field label="Next action date" htmlFor="tf-next-date">
              <input id="tf-next-date" type="date" className="input" value={form.nextActionDate} onChange={(e) => set('nextActionDate', e.target.value)} />
            </Field>
          </div>

          {!isEdit && (
            <div className="stack sm">
              <Toggle checked={form.followUpEnabled} onChange={(v) => set('followUpEnabled', v)} label="Create a follow-up" description="A separate follow-up linked to this task" />
              {form.followUpEnabled && (
                <div className="card card-pad stack sm">
                  <input className="input" placeholder="Follow-up title (optional)" value={form.followUp.title ?? ''} onChange={(e) => set('followUp', { ...form.followUp, title: e.target.value })} aria-label="Follow-up title" />
                  <DatePicker value={form.followUp.due} onChange={(d) => set('followUp', { ...form.followUp, due: d })} allowNone={false} label="Follow-up on" compact showTime={false} />
                  {errors.followUp && <span className="error-text">{errors.followUp}</span>}
                </div>
              )}
            </div>
          )}

          <Field label="Description" htmlFor="tf-desc">
            <textarea id="tf-desc" className="textarea" value={form.description} maxLength={10000} onChange={(e) => set('description', e.target.value)} />
          </Field>
          {isEdit && (
            <Field label="Notes" htmlFor="tf-notes">
              <textarea id="tf-notes" className="textarea" value={form.notes} maxLength={20000} onChange={(e) => set('notes', e.target.value)} />
            </Field>
          )}
          <button type="submit" hidden aria-hidden tabIndex={-1} />
        </form>
      )}
    </Modal>
  );
}

function ConflictDiff({ mine, theirs }: { mine: FormState; theirs: FormState }) {
  const fields: Array<[keyof FormState, string]> = [
    ['title', 'Title'],
    ['priority', 'Priority'],
    ['due', 'Due'],
    ['status', 'Status'],
    ['assigneeId', 'Assignee'],
    ['description', 'Description'],
    ['notes', 'Notes'],
    ['tags', 'Tags'],
  ];
  const diffs = fields.filter(([k]) => JSON.stringify(mine[k]) !== JSON.stringify(theirs[k]));
  if (!diffs.length) return <span className="small">No field differences — you can keep your changes safely.</span>;
  const show = (v: unknown) => (typeof v === 'string' ? v || '—' : JSON.stringify(v));
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Your version</th>
            <th>Latest saved</th>
          </tr>
        </thead>
        <tbody>
          {diffs.map(([k, label]) => (
            <tr key={k}>
              <td>{label}</td>
              <td className="pre">{show(mine[k])}</td>
              <td className="pre">{show(theirs[k])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
